import React from 'react';
import { Shield, FileText, Clock, CheckCircle, AlertTriangle, Users, Book, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const ProcessServingGuide = () => {
  const guideSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Complete Guide to Process Serving in California 2025",
    "description": "Comprehensive guide to process serving in California covering legal requirements, procedures, registration, methods, and best practices for law firms and legal professionals.",
    "author": {
      "@type": "Organization",
      "name": "Legal Document Specialist"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Legal Document Specialist",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who can serve legal documents in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In California, any person who is at least 18 years old and not a party to the case can serve legal documents. However, anyone who serves more than 10 papers for compensation within a calendar year must register as a professional process server with the county clerk, undergo a Live Scan fingerprint background check, and post a $2,000 surety bond."
        }
      },
      {
        "@type": "Question",
        "name": "What are the different methods of service in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "California recognizes several service methods: Personal Service (handing documents directly to the individual), Substituted Service (leaving documents with a responsible person at home/business plus mailing), Service by Mail (with acknowledgment), Service by Publication (for unknown addresses after court approval), and Electronic Service (with consent). Personal service is the gold standard and often required for initial documents."
        }
      },
      {
        "@type": "Question",
        "name": "How long do you have to serve legal documents in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In California, summons and complaints must be served within 60 days of filing the complaint. Other documents have varying time requirements depending on the type of proceeding and document. Failure to serve within required timeframes can result in case dismissal or other sanctions."
        }
      },
      {
        "@type": "Question",
        "name": "What is proof of service and why is it important?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Proof of service is a legal document filed with the court that details when, where, how, and to whom legal documents were served. It's critical because it provides official record that proper service occurred, protecting due process rights and allowing the case to proceed. The proof must include specific information about the service method, date, time, location, and recipient details."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if someone cannot be served?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "If personal service cannot be completed after diligent attempts, California allows substituted service or service by mail. If the person's location is unknown after thorough investigation, the court may authorize service by publication in a newspaper. Professional process servers can conduct skip tracing to locate hard-to-find individuals. Proof of diligent attempts must be documented."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to hire a professional process server?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "While not always legally required, hiring a professional registered process server is highly recommended for several reasons: they understand California's complex service rules, provide reliable proof of service, have experience with difficult serves, are properly bonded and insured, and can testify in court if service is challenged. For law firms and businesses, professional process servers ensure compliance and reduce liability."
        }
      }
    ]
  };

  React.useEffect(() => {
    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.textContent = JSON.stringify(guideSchema);
    document.head.appendChild(articleScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(guideSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="Complete Guide to Process Serving in California 2025 | Legal Requirements & Procedures"
        description="Comprehensive guide to California process serving: legal requirements, registration, service methods, procedures, and best practices for law firms. Expert insights for 2025."
        keywords="process serving California guide, California process server requirements, legal service procedures, process server registration, service of process California"
        canonical="/resources/process-serving-guide"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'var(--bg-card)',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              marginBottom: '1.5rem',
              border: '1px solid var(--border-medium)'
            }}>
              <Book size={16} color="var(--brand-primary)" />
              <span className="caption" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>
                Pillar Content Guide
              </span>
            </div>
            
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)',
              fontSize: 'clamp(2rem, 4vw, 3.5rem)'
            }}>
              Complete Guide to Process Serving in California 2025
            </h1>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Everything law firms and legal professionals need to know about process serving requirements, procedures, methods, and best practices in California.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/process-serving" className="btn-primary">
                Our Process Serving Services
              </Link>
              <a href="/quote" className="btn-secondary">
                Request a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Guide Contents
            </h2>
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <ul style={{
                listStyle: 'none',
                paddingLeft: '0',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                {[
                  'What is Process Serving?',
                  'Legal Requirements in California',
                  'Registration & Licensing',
                  'Service Methods Explained',
                  'Proof of Service Requirements',
                  'Timeline & Deadlines',
                  'Best Practices for Law Firms',
                  'Common Challenges & Solutions'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.5rem 0'
                  }}>
                    <CheckCircle size={18} color="var(--brand-primary)" />
                    <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What is Process Serving */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              What is Process Serving?
            </h2>
            
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Process serving is the legal procedure of delivering court documents to parties involved in a legal proceeding. This fundamental aspect of the American legal system ensures that all parties receive proper notice of legal actions against them, protecting their constitutional right to due process. In California, process serving is governed by state law and court rules that specify who can serve documents, how they must be served, and what proof is required.
            </p>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The term "service of process" refers specifically to delivering legal documents such as summons, complaints, subpoenas, writs, and other court orders. Proper service is not just a formality—it's a constitutional requirement that enables the court to exercise jurisdiction over the parties and proceed with the case. Without proper service, court proceedings cannot move forward, and any judgments obtained may be invalid.
            </p>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '2px solid var(--brand-primary)',
              marginTop: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                <Scale size={24} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Constitutional Foundation
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Assembly Bill 747 (2025) reaffirms that lawful service of process is a foundational due process requirement protecting defendants' rights under the U.S. Constitution. Proper service ensures fair notice and opportunity to be heard before any legal judgment can be entered.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Legal Requirements in California
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginBottom: '3rem'
            }}>
              {[
                {
                  icon: <Users size={24} />,
                  title: 'Age & Neutrality',
                  description: 'Process servers must be at least 18 years old and cannot be a party to the case. This ensures neutrality and legal capacity to serve documents.'
                },
                {
                  icon: <Shield size={24} />,
                  title: 'Registration Requirement',
                  description: 'Anyone serving more than 10 papers for compensation within a calendar year must register as a professional process server with the county clerk.'
                },
                {
                  icon: <FileText size={24} />,
                  title: 'Background Check',
                  description: 'Registration requires Live Scan fingerprinting to confirm no felony convictions. This protects public safety and maintains professional standards.'
                },
                {
                  icon: <AlertTriangle size={24} />,
                  title: '$2,000 Surety Bond',
                  description: 'Registered process servers must post a $2,000 surety bond valid for two years, providing financial protection and accountability.'
                }
              ].map((req, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    color: 'white'
                  }}>
                    {req.icon}
                  </div>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {req.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {req.description}
                  </p>
                </div>
              ))}
            </div>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Registration Process
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
                Professional process servers must file a verified Certificate of Registration with the county clerk where they reside or have their principal business address. The registration is valid for two years and requires renewal with updated Live Scan fingerprinting if expired. Registrations are public records, providing transparency and accountability to the courts and public.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Process servers must maintain compliance with all legal duties and keep current with legislative changes. Failure to maintain proper registration can result in penalties and inability to serve legal documents professionally in California.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Methods */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Service Methods Explained
            </h2>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', lineHeight: '1.8' }}>
              California law recognizes several methods of service, each with specific requirements and appropriate use cases. Understanding these methods is crucial for ensuring proper service and avoiding delays or dismissals.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {[
                {
                  title: 'Personal Service',
                  badge: 'Gold Standard',
                  description: 'Personal service involves handing documents directly to the named individual. This is considered the most reliable method and is often required for initial documents like summons and complaints. The process server must confirm the identity of the recipient and physically hand them the documents.',
                  requirements: [
                    'Direct hand-to-hand delivery to the named party',
                    'Confirmation of recipient identity',
                    'Immediate and complete delivery of all documents',
                    'Required for most initial court documents'
                  ]
                },
                {
                  title: 'Substituted Service',
                  badge: 'Alternative Method',
                  description: 'If personal service is impractical after diligent attempts, California allows substituted service. Documents may be left with a responsible person at the recipient\'s home or business, followed by mailing a copy to the same address. This method requires proof of multiple service attempts.',
                  requirements: [
                    'Multiple attempts at personal service first (different times/days)',
                    'Leave documents with responsible person at home or office',
                    'Mail a copy to the same address',
                    'File declaration of due diligence with proof of attempts',
                    'Wait period before service is deemed complete'
                  ]
                },
                {
                  title: 'Service by Mail',
                  badge: 'With Acknowledgment',
                  description: 'Certain documents can be served by mail with acknowledgment of receipt. The recipient must sign and return an acknowledgment form. This method is less common for initial service but may be used for subsequent documents or in specific situations.',
                  requirements: [
                    'Certified or registered mail',
                    'Acknowledgment of receipt form included',
                    'Return receipt required',
                    'Not available for all document types'
                  ]
                },
                {
                  title: 'Service by Publication',
                  badge: 'Last Resort',
                  description: 'When the defendant\'s location is unknown despite thorough investigation, courts may authorize service by publication in a newspaper. This requires court approval and proof that reasonable attempts to locate the individual have failed.',
                  requirements: [
                    'Court order required',
                    'Proof of diligent search efforts',
                    'Publication in approved newspaper',
                    'Multiple publication dates required',
                    'Additional mailing if partial address known'
                  ]
                },
                {
                  title: 'Electronic Service',
                  badge: 'With Consent',
                  description: 'With consent from all parties, electronic service via email is permitted for certain documents. As of July 1, 2025, attorneys must accept electronic service for court notices in civil and family matters.',
                  requirements: [
                    'Written consent from recipient required',
                    'Confirmation of electronic delivery',
                    'Must maintain email address on file with court',
                    'Two additional court days for response time',
                    'Not available for all document types'
                  ]
                }
              ].map((method, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '2.5rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <h3 className="heading-3" style={{ margin: 0 }}>
                      {method.title}
                    </h3>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: 'var(--brand-primary)',
                      color: 'white',
                      borderRadius: '50px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {method.badge}
                    </span>
                  </div>
                  
                  <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
                    {method.description}
                  </p>
                  
                  <h4 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    Requirements:
                  </h4>
                  <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                    {method.requirements.map((req, rIndex) => (
                      <li key={rIndex} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        marginBottom: '0.75rem'
                      }}>
                        <CheckCircle size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Proof of Service */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Proof of Service Requirements
            </h2>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              After serving documents, the process server must complete and file a proof of service form with the court. This legal document provides official record that proper service occurred and is critical for the case to proceed. The proof of service must include specific information to be valid.
            </p>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Required Information in Proof of Service
              </h3>
              <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                {[
                  'Date and time of service',
                  'Location where service occurred (complete address)',
                  'Name of person served',
                  'Method of service used (personal, substituted, etc.)',
                  'Description of person served (age, identifying characteristics)',
                  'Complete list of documents served',
                  'Process server\'s signature and declaration under penalty of perjury',
                  'For substituted service: name and relationship of person accepting documents',
                  'For substituted service: declaration of due diligence with attempt details',
                  'Mailing information if applicable'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <FileText size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Why Proof of Service Matters
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
                Proof of service serves multiple critical functions in legal proceedings:
              </p>
              <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                {[
                  'Establishes court jurisdiction over the parties',
                  'Protects due process rights of all parties',
                  'Creates official record for court files',
                  'Prevents defendant claims of improper notice',
                  'Enables case to proceed to next stages',
                  'Provides evidence if service is challenged',
                  'Documents compliance with legal requirements',
                  'Allows process server to testify if needed'
                ].map((reason, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <Shield size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline & Deadlines */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Timeline & Deadlines
            </h2>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '2px solid var(--brand-primary)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                <Clock size={24} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Critical: 60-Day Rule
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                In California, summons and complaints must be served within 60 days of filing the complaint. Failure to serve within this timeframe can result in case dismissal. This is one of the most important deadlines in California civil procedure and requires prompt action by law firms.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {[
                {
                  document: 'Summons & Complaint',
                  deadline: '60 days from filing',
                  notes: 'Most critical deadline. Extensions possible but require court approval.'
                },
                {
                  document: 'Subpoenas',
                  deadline: 'Reasonable time before hearing/deposition',
                  notes: 'Generally 10 days minimum for deposition subpoenas; varies by subpoena type.'
                },
                {
                  document: 'Motions',
                  deadline: 'Per Code of Civil Procedure',
                  notes: 'Varies by motion type; typically 16-21 court days before hearing.'
                },
                {
                  document: 'Notice of Entry of Judgment',
                  deadline: 'After judgment entered',
                  notes: 'Starts appeal period; must be served promptly.'
                },
                {
                  document: 'Writs & Orders',
                  deadline: 'As specified in order',
                  notes: 'Immediate service often required; check specific order requirements.'
                }
              ].map((item, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid var(--border-medium)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <h4 className="heading-4" style={{ marginBottom: '0.5rem' }}>
                      {item.document}
                    </h4>
                    <p style={{ color: 'var(--brand-primary)', fontWeight: '600', fontSize: '0.9rem' }}>
                      {item.deadline}
                    </p>
                  </div>
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {item.notes}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem' }}>
              Best Practices for Law Firms
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  title: 'Hire Registered Process Servers',
                  description: 'Always use registered, bonded, and insured professional process servers. Verify their registration status with the county clerk and confirm their insurance coverage.'
                },
                {
                  title: 'Provide Complete Information',
                  description: 'Give process servers all available information about the person to be served: full name, address, physical description, work schedule, vehicle information, and any relevant details.'
                },
                {
                  title: 'Start Early',
                  description: 'Don\'t wait until the last minute. Begin service attempts immediately after filing to allow time for multiple attempts and alternative methods if needed.'
                },
                {
                  title: 'Document Everything',
                  description: 'Maintain detailed records of all service attempts, communications with process servers, and any issues encountered. This documentation is crucial if service is challenged.'
                },
                {
                  title: 'Understand Service Methods',
                  description: 'Know which service method is appropriate for each document type and situation. Personal service may be required for some documents while substituted service is acceptable for others.'
                },
                {
                  title: 'Review Proof of Service',
                  description: 'Carefully review all proofs of service for completeness and accuracy before filing with the court. Incomplete or incorrect proofs can delay proceedings.'
                },
                {
                  title: 'Use Skip Tracing When Needed',
                  description: 'For hard-to-locate individuals, use professional skip tracing services early. Don\'t waste time on unsuccessful service attempts when the address is outdated.'
                },
                {
                  title: 'Plan for Substituted Service',
                  description: 'If personal service may be difficult, start documenting service attempts immediately to establish the due diligence required for substituted service.'
                },
                {
                  title: 'Stay Current on Rules',
                  description: 'California service rules change. Subscribe to court updates and work with process servers who stay current on procedural requirements and rule changes.'
                }
              ].map((practice, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {practice.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {practice.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'Who can serve legal documents in California?',
                answer: 'In California, any person who is at least 18 years old and not a party to the case can serve legal documents. However, anyone who serves more than 10 papers for compensation within a calendar year must register as a professional process server with the county clerk, undergo a Live Scan fingerprint background check, and post a $2,000 surety bond.'
              },
              {
                question: 'What are the different methods of service in California?',
                answer: 'California recognizes several service methods: Personal Service (handing documents directly to the individual), Substituted Service (leaving documents with a responsible person at home/business plus mailing), Service by Mail (with acknowledgment), Service by Publication (for unknown addresses after court approval), and Electronic Service (with consent). Personal service is the gold standard and often required for initial documents.'
              },
              {
                question: 'How long do you have to serve legal documents in California?',
                answer: 'In California, summons and complaints must be served within 60 days of filing the complaint. Other documents have varying time requirements depending on the type of proceeding and document. Failure to serve within required timeframes can result in case dismissal or other sanctions.'
              },
              {
                question: 'What is proof of service and why is it important?',
                answer: 'Proof of service is a legal document filed with the court that details when, where, how, and to whom legal documents were served. It\'s critical because it provides official record that proper service occurred, protecting due process rights and allowing the case to proceed. The proof must include specific information about the service method, date, time, location, and recipient details.'
              },
              {
                question: 'What happens if someone cannot be served?',
                answer: 'If personal service cannot be completed after diligent attempts, California allows substituted service or service by mail. If the person\'s location is unknown after thorough investigation, the court may authorize service by publication in a newspaper. Professional process servers can conduct skip tracing to locate hard-to-find individuals. Proof of diligent attempts must be documented.'
              },
              {
                question: 'Do I need to hire a professional process server?',
                answer: 'While not always legally required, hiring a professional registered process server is highly recommended for several reasons: they understand California\'s complex service rules, provide reliable proof of service, have experience with difficult serves, are properly bonded and insured, and can testify in court if service is challenged. For law firms and businesses, professional process servers ensure compliance and reduce liability.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need Professional Process Serving?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Registered, bonded, and insured process servers throughout California
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/process-serving"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              View Process Serving Services
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProcessServingGuide;
